// import node module libraries
import {Fragment, useEffect, useRef, useState} from 'react';
import {Breadcrumb, Button, Card, Col, Form, Offcanvas, Row} from 'react-bootstrap';

// import full calendar and its plugins
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import SalledeclasseService from '../services/SalledeclasseService';
import EmploidutempsService from '../services/EmploidutempsService';
import AnneescolaireService from '../services/AnneescolaireService';
import MatiereService from '../services/MatiereService';
import CompetenceService from '../services/CompetenceService';
import ExamenService from '../services/ExamenService';
import OffcanvasAddEmploidutempsForm from "../emploidutemps/add-new-emploidutemps/OffcanvasAddEmploidutempsForm"; // Import French locale
// import sub components

const Emploidutemps = () => {
    // Define required states
    const [calendarApi, setCalendarApi] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [classOptions, setClassOptions] = useState([]);
    const [emploidutemps, setEmploidutempsOptions] = useState([]);
    const [currentSchoolYear, setCurrentSchoolYear] = useState(null);

    useEffect(() => {
        const fetchCurrentSchoolYear = async () => {
            try {
                const schoolYear = await AnneescolaireService.getCurrentAnneescolaire();
                setCurrentSchoolYear(schoolYear.data);
            } catch (error) {
                console.error(
                    "Erreur lors de la récupération de l'année scolaire en cours:",
                    error
                );
                alert(
                    `Erreur lors de la récupération de l'année scolaire en cours. Détails de l'erreur : ${error.message}`
                );
            }
        };

        fetchCurrentSchoolYear();
    }, []);

    useEffect(() => {
        const fetchClassOptions = async () => {
            try {
                const classes = await SalledeclasseService.getClassOptions();
                setClassOptions(classes);
            } catch (error) {
                console.error('Error fetching class options', error);
            }
        };

        fetchClassOptions();
    }, []);

    useEffect(() => {
        const fetchEmploidutempsOptions = async () => {
            try {
                let emploidutemps = await EmploidutempsService.getAllEmploidutemps();
                // Filter based on selected class and current school year

                emploidutemps = emploidutemps.filter(
                    (emploi) =>
                        emploi.salledeclasse === Number(selectedClass) &&
                        emploi.anneescolaire === Number(currentSchoolYear.id)
                );

                const enhancedEmploidutemps = await Promise.all(
                    emploidutemps.map(async (emploi) => {
                        const anneescolaire = await AnneescolaireService.getAnneescolaire(
                            emploi.anneescolaire
                        );
                        const salledeclasse = await SalledeclasseService.getSalledeclasse(
                            emploi.salledeclasse
                        );

                        const matiere = emploi.matiere
                            ? await MatiereService.getMatiere(emploi.matiere)
                            : null;
                        const competence = emploi.competence
                            ? await CompetenceService.getCompetence(emploi.competence)
                            : null;
                        const examen = emploi.examen
                            ? await ExamenService.getExamen(emploi.examen)
                            : null;
                        return {
                            ...emploi,
                            anneescolaire,
                            salledeclasse,
                            matiere,
                            competence,
                            examen
                        };
                    })
                );
                setEmploidutempsOptions(enhancedEmploidutemps);
            } catch (error) {
                console.error('Error fetching emploi du temps options', error);
            }
        };

        // Run this only if both selectedClass and currentSchoolYear are defined
        if (selectedClass && currentSchoolYear) {
            fetchEmploidutempsOptions();
        }
    }, [selectedClass, currentSchoolYear]);

    const [show, setShow] = useState(false);

    // Ajouter cette fonction pour créer des événements pour chaque jour entre deux dates
    const createEventsForPeriod = (startDate, endDate, emploidutempsForClass) => {
        let date = new Date(startDate);
        let events = [];

        while (date <= endDate) {
            // Filter emploi du temps entries for the current day
            let emploidutempsForDay = emploidutempsForClass.filter(
                (emploi) => emploi.jour === date.getDay()
            );

            // Create an event for each emploi du temps entry
            emploidutempsForDay.forEach((emploi) => {
                let event = convertDataToEvent(emploi, date);
                events.push(event);
            });

            // Increment date by 1 day
            date.setDate(date.getDate() + 1);
        }

        return events;
    };

    const convertDataToEvent = (data, date) => {
        // Créer l'objet date de début et de fin
        const start = new Date(date);
        const [hours, minutes] = data.heure_debut.split(':');
        start.setHours(hours);
        start.setMinutes(minutes);

        const end = new Date(date);
        const [hoursEnd, minutesEnd] = data.heure_fin.split(':');
        end.setHours(hoursEnd);
        end.setMinutes(minutesEnd);

        // Définir la couleur en fonction du type d'événement
        let color;
        switch (data.emploi_type) {
            case 'cours':
                color = 'blue';
                break;
            case 'pause':
                color = 'red';
                break;
            case 'conge':
                color = 'green';
                break;
            case 'examen':
                color = 'yellow';
                break;
            default:
                color = 'grey';
        }

        // Retourner l'événement dans le format attendu par FullCalendar
        return {
            title: data.emploi_type, // Vous pouvez changer cela en fonction de vos besoins
            start: start.toISOString(),
            end: end.toISOString(),
            color: color,
            extendedProps: {
                salledeclasse: data.salledeclasse,
                matiere: data.matiere,
                competence: data.competence,
                examen: data.examen
            }
        };
    };

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Emploidutemps Refs
    const calendarRef = useRef(null);

    // useEffect hook to check calendarApi Update
    useEffect(() => {
        if (calendarApi) {
            calendarApi.removeAllEvents();

            // Filter emploidutemps by selected class
            let emploidutempsForClass = emploidutemps.filter(
                (emploi) => emploi.salledeclasse === selectedClass
            );

            // Create events for each period of the school year
            let trimesters = [
                {start: 'date_debut', end: 'fin_trimestre_1'},
                {start: 'debut_trimestre_2', end: 'fin_trimestre_2'},
                {start: 'debut_trimestre_3', end: 'date_fin'}
            ];

            trimesters.forEach((trimester) => {
                let events = createEventsForPeriod(
                    trimester.start,
                    trimester.end,
                    emploidutempsForClass
                );
                events.forEach((event) => calendarApi.addEvent(event));
            });
        }
    }, [calendarApi, emploidutemps, selectedClass]);

    // Blank Event Object
    var date = new Date();
    const blankEvent = {
        title: '',
        start: date.setDate(date.getDate()),
        end: date.setDate(date.getDate() + 1),
        allDay: false,
        url: '',
        extendedProps: {
            category: '',
            location: '',
            description: ''
        }
    };

    // Emploidutemps Options or Properties
    const calendarOptions = {
        ref: calendarRef,
        events: '',
        plugins: [timeGridPlugin, interactionPlugin, listPlugin],
        initialView: 'timeGridWeek',
        headerToolbar: {
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridWeek,timeGridDay,listWeek'
        },
        editable: true,
        eventResizableFromStart: true,
        dayMaxEvents: 5,
        navLinks: true,
        slotMinTime: '07:00:00', // L'emploi du temps commence à 7:00 AM
        slotMaxTime: '18:00:00', // L'emploi du temps se termine à 18:00 PM (1 heure après la dernière heure pour pouvoir afficher le bloc de 17:00 à 18:00)
        locale: frLocale, // Définir le français comme langue
        firstDay: 1 // Mettre Lundi comme premier jour de la semaine (0 pour Dimanche, 1 pour Lundi, etc.)
    };

    return (
        <Fragment>
            <Row>
                <Col lg={12} md={12} sm={12}>
                    <div className="border-bottom pb-4 mb-4 d-md-flex align-items-center justify-content-between">
                        <div className="mb-3 mb-md-0">
                            <h1 className="mb-1 h2 fw-bold">Emploi de temps</h1>
                            <Breadcrumb>
                                <Breadcrumb.Item href="/">
                                    Accueil
                                </Breadcrumb.Item>
                                <Breadcrumb.Item active>Emploi de temps</Breadcrumb.Item>
                            </Breadcrumb>
                        </div>

                        <div className="d-flex align-items-center justify-content-between">
                            <div>
                                <div className="d-flex p-3">
                                    <div className="me-3">
                                        <Form.Select
                                            value={selectedClass}
                                            onChange={(e) => setSelectedClass(e.target.value)}
                                        >
                                            <option value="">Sélectionnez une classe</option>
                                            {classOptions.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </div>
                                    <Button onClick={handleShow}>Gérer emploi de temps</Button>
                                </div>
                                <Offcanvas
                                    show={show}
                                    onHide={handleClose}
                                    placement="end"
                                    name="end"
                                    style={{width: '600px'}}
                                >
                                    <Offcanvas.Header closeButton>
                                        <Offcanvas.Title as="h3">
                                            Organiser l'emploi de temps
                                        </Offcanvas.Title>
                                    </Offcanvas.Header>
                                    <Offcanvas.Body className="pt-0">
                                        <OffcanvasAddEmploidutempsForm onClick={handleClose}/>
                                    </Offcanvas.Body>
                                </Offcanvas>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            <Row>
                <Col xl={12} lg={12} md={12} xs={12}>
                    <Card>
                        {/* Emploidutemps */}
                        <FullCalendar {...calendarOptions} />
                    </Card>
                </Col>
            </Row>
        </Fragment>
    );
};
export default Emploidutemps;
